import { useMemo } from 'react'
import { isNil } from 'lodash-es'
import { useQuery as useReactQuery } from '@tanstack/react-query'

import { useIdentity } from '@/hooks/auth/useIdentity'
import { GQL_CLIENT } from 'src/graphqlClient'
import { AccountType } from 'src/utils/enums'
import {
  ArchitectProfile,
  ClientProfile,
  CollaboratorProfile,
  CompanyProfile,
  SalesmanProfile,
  getArchitectProfile,
  getClientProfile,
  getCollaboratorProfile,
  getCompanyProfile,
  getSalesmanProfile,
} from 'src/services/gql/profiles'
import type { Account } from 'types'

export type ProfileData =
  | ArchitectProfile
  | CollaboratorProfile
  | ClientProfile
  | SalesmanProfile
  | CompanyProfile

const fetchAccountProfile = (
  accountType: string | undefined,
  id: number,
): Promise<ProfileData> => {
  switch (accountType) {
    case AccountType.ARCHITECT:
      return getArchitectProfile(GQL_CLIENT, id)
    case AccountType.COLLABORATOR:
      return getCollaboratorProfile(GQL_CLIENT, id)
    case AccountType.CLIENT:
      return getClientProfile(GQL_CLIENT, id)
    case AccountType.SALESMAN:
      return getSalesmanProfile(GQL_CLIENT, id)
    case AccountType.SOCIETY:
      return getCompanyProfile(GQL_CLIENT, id)
    default:
      return Promise.reject(new Error('Account type not found'))
  }
}

// The role relation (architect/collaborator/client/salesmen/company) can be
// null even when account_type is set — e.g. a provisioned-but-unlinked account
// or a deleted role row. Never dereference it unguarded.
const personFirstName = (person?: {
  first_name?: string | null
}): string => person?.first_name?.trim() ?? ''

const personFullName = (person?: {
  first_name?: string | null
  last_name?: string | null
}): string =>
  [person?.first_name, person?.last_name].filter(Boolean).join(' ').trim()

const firstNameOf = (profile: ProfileData | undefined): string => {
  if (!profile) return ''
  switch (profile.account_type) {
    case 'ARCHITECT':
      return personFirstName(profile.architect)
    case 'COLLABORATOR':
      return personFirstName(profile.collaborator)
    case 'CLIENT':
      return personFirstName(profile.client)
    case 'SALESMAN':
      return personFirstName(profile.salesmen)
    case 'SOCIETY':
      return profile.company?.name?.trim() ?? ''
    default:
      return ''
  }
}

const fullNameOf = (profile: ProfileData | undefined): string => {
  if (!profile) return ''
  switch (profile.account_type) {
    case 'ARCHITECT':
      return personFullName(profile.architect)
    case 'COLLABORATOR':
      return personFullName(profile.collaborator)
    case 'CLIENT':
      return personFullName(profile.client)
    case 'SALESMAN':
      return personFullName(profile.salesmen)
    case 'SOCIETY':
      return profile.company?.name?.trim() || 'Company'
    default:
      return ''
  }
}

/**
 * The logged-in account's profile, resolved dynamically from its
 * `account_type` by following the matching relation
 * (architect/collaborator/client/salesmen/company) on `accounts_by_pk`.
 *
 * This is the single source of truth for the user's display name — shared by
 * the sider avatar and the projects-hub greeting via the same
 * `account-header-info` React Query key, so both render the same name from one
 * cached fetch.
 */
export const useAccountProfile = () => {
  const { data: identity } = useIdentity<Account>()
  const accountType = identity?.account_type

  const { data: accountInfo, isLoading } = useReactQuery<{ data: ProfileData }>({
    queryKey: ['account-header-info', identity?.id, accountType],
    enabled: !isNil(identity) && !isNil(accountType),
    queryFn: async () =>
      fetchAccountProfile(accountType, identity!.id).then((data) => ({ data })),
  })

  const profile = accountInfo?.data

  const firstName = useMemo(() => firstNameOf(profile), [profile])
  const fullName = useMemo(() => fullNameOf(profile), [profile])

  return { profile, accountType, firstName, fullName, isLoading }
}
